/**
 * Centralized hook for entity table data loading
 * 
 * This hook provides consistent behavior across all entity tables:
 * - Prevents duplicate API calls
 * - Handles auth resolution
 * - Manages entity type changes
 * - Handles pagination
 * - Clears stale data on entity type changes
 * 
 * Used by:
 * - DynamicTableView
 * - PersonManagement
 * - useLocalityManagement
 * - GroupsManagement
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ListResponse } from '../components/common/GenericTable';
import { extractErrorMessage } from '../utils/apiUtils';
import type { ScimEntity } from '../types/ScimEntity';
import { debugVerboseLog } from '../utils/debugLog';

export interface EntityTableLoaderOptions<TData = ScimEntity> {
    /**
     * Entity type identifier (e.g., 'Person', 'Class', 'Group')
     */
    entityType: string;

    /**
     * Whether authentication has been resolved
     */
    authResolved: boolean;

    /**
     * Function to load data for a specific page.
     * Receives (page, abortSignal?, ...args). Implementations should pass abortSignal to fetch/axios when supported.
     */
    loadData: (page: number, abortSignal?: AbortSignal, ...args: any[]) => Promise<{
        data?: TData[];
        items?: TData[];
        totalResults: number;
        currentPage: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        [key: string]: any;
    }>;

    /**
     * Initial page number (default: 1)
     */
    initialPage?: number;

    /**
     * Items per page (default: 25)
     */
    itemsPerPage?: number;

    /**
     * Additional arguments to pass to loadData (can be a function that returns args)
     * This allows the args to be dynamic based on current state
     */
    loadDataArgs?: any[] | (() => any[]);

    /**
     * Whether to clear data when entity type changes (default: true)
     */
    clearDataOnEntityTypeChange?: boolean;

    /**
     * Whether to automatically load on mount/auth resolution (default: true)
     * If false, you must manually call reload()
     */
    autoLoad?: boolean;

    /**
     * Callback to check if load should be skipped (e.g., during search operations)
     * Return true to skip loading
     */
    shouldSkipLoad?: () => boolean;

    /**
     * When using per-entity entitlement: set to true when entitlement has resolved (e.g. allowedTableAttributeIds !== null).
     * When this flips to true the effect re-runs and loads once; avoids double load from effect + onResolved.
     */
    entitlementResolved?: boolean;

    /**
     * When this changes (e.g. on-behalf-of toggled), table clears and reloads so data matches the new effective user.
     */
    refreshKey?: string;
}

export interface EntityTableLoaderResult<TData = ScimEntity> {
    // Data state
    data: TData[];
    loading: boolean;
    error: string | null;

    /** Full list response object (items + list-level $ and other props) for passing to detail dialog etc. */
    listResponse: ListResponse<TData> | null;

    // Pagination state
    currentPage: number;
    totalPages: number;
    totalResults: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Controls
    setCurrentPage: (page: number) => void;
    setError: (error: string | null) => void;
    reload: () => Promise<void>;
    loadPage: (page: number) => Promise<void>;
}

export function useEntityTableLoader<TData = ScimEntity>(
    options: EntityTableLoaderOptions<TData>
): EntityTableLoaderResult<TData> {
    const {
        entityType,
        authResolved,
        loadData,
        initialPage = 1,
        itemsPerPage = 25,
        loadDataArgs = [],
        clearDataOnEntityTypeChange = true,
        autoLoad = true,
        shouldSkipLoad,
        entitlementResolved,
        refreshKey
    } = options;

    // Page state
    const [currentPage, setCurrentPage] = useState(initialPage);
    // Manual error state (for setError from consumers)
    const [manualError, setManualError] = useState<string | null>(null);
    // Fetch version counter: incremented to force refetch without changing page
    const [fetchVersion, setFetchVersion] = useState(0);

    // Refs for the callback-based loadData pattern (TanStack Query's queryFn captures via closure)
    const loadDataRef = useRef(loadData);
    useEffect(() => { loadDataRef.current = loadData; }, [loadData]);

    const loadDataArgsRef = useRef<any[] | (() => any[])>(loadDataArgs);
    useEffect(() => { loadDataArgsRef.current = loadDataArgs; }, [loadDataArgs]);

    const getLoadDataArgs = useCallback(() => {
        if (typeof loadDataArgsRef.current === 'function') {
            return loadDataArgsRef.current();
        }
        return loadDataArgsRef.current || [];
    }, []);

    // Reset page when entity type changes
    const prevEntityTypeRef = useRef(entityType);
    useEffect(() => {
        const prevNorm = prevEntityTypeRef.current?.toLowerCase();
        const currentNorm = entityType?.toLowerCase();
        if (prevNorm !== currentNorm) {
            debugVerboseLog(`🔄 useEntityTableLoader: Entity type changed from ${prevEntityTypeRef.current} to ${entityType}, resetting page`);
            prevEntityTypeRef.current = entityType;
            setCurrentPage(initialPage);
            setManualError(null);
            setFetchVersion(v => v + 1);
        }
    }, [entityType, initialPage]);

    // Reset on refreshKey change
    const prevRefreshKeyRef = useRef(refreshKey);
    useEffect(() => {
        if (prevRefreshKeyRef.current !== undefined && refreshKey !== prevRefreshKeyRef.current) {
            debugVerboseLog(`🔄 useEntityTableLoader: refreshKey changed, resetting`);
            prevRefreshKeyRef.current = refreshKey;
            setCurrentPage(initialPage);
            setManualError(null);
            setFetchVersion(v => v + 1);
        } else {
            prevRefreshKeyRef.current = refreshKey;
        }
    }, [refreshKey, initialPage]);

    // Determine if query should be enabled
    const queryEnabled = useMemo(() => {
        if (!autoLoad) return false;
        if (!authResolved) return false;
        if (shouldSkipLoad && shouldSkipLoad()) return false;
        if (entitlementResolved === false) return false;
        return true;
    }, [autoLoad, authResolved, shouldSkipLoad, entitlementResolved]);

    // TanStack Query for data fetching
    const query = useQuery({
        queryKey: ['entities-table', entityType?.toLowerCase(), currentPage, refreshKey, fetchVersion] as const,
        queryFn: async ({ signal }) => {
            const args = getLoadDataArgs();
            debugVerboseLog(`✅ useEntityTableLoader: Fetching ${entityType} page ${currentPage}`);
            return loadDataRef.current(currentPage, signal, ...args);
        },
        enabled: queryEnabled,
        placeholderData: (prevData, prevQuery) => {
            // Keep previous data for page changes, not for entity type changes
            if (!prevQuery) return undefined;
            const prevKey = prevQuery.queryKey as readonly unknown[];
            const prevEntityType = prevKey[1];
            return prevEntityType === entityType?.toLowerCase() ? prevData : undefined;
        },
        retry: 0,
    });

    // Derive data from query result
    const queryData = query.data;
    const rows = useMemo(() => {
        if (!queryData) return [] as TData[];
        return (queryData.items ?? queryData.data ?? []) as TData[];
    }, [queryData]);

    const listResponse = useMemo((): ListResponse<TData> | null => {
        if (!queryData || typeof queryData !== 'object') return null;
        return { ...queryData, items: rows } as ListResponse<TData>;
    }, [queryData, rows]);

    // Derive loading: true when fetching OR when waiting for entitlement/auth
    const isWaitingForPrereqs = autoLoad && (!authResolved || (shouldSkipLoad?.() ?? false) || entitlementResolved === false);
    const loading = query.isFetching || (isWaitingForPrereqs && !query.data);

    // Derive error: prefer manual error, then query error
    const error = manualError ?? (query.error ? (extractErrorMessage(query.error) || `Failed to load ${entityType}`) : null);

    // Pagination derived from query data
    const totalPages = queryData?.totalPages ?? 1;
    const totalResults = queryData?.totalResults ?? 0;
    const hasNextPage = queryData?.hasNextPage ?? false;
    const hasPreviousPage = queryData?.hasPreviousPage ?? false;

    // Imperative controls
    const reload = useCallback(async () => {
        setManualError(null);
        setFetchVersion(v => v + 1);
    }, []);

    const loadPage = useCallback(async (page: number) => {
        setManualError(null);
        setCurrentPage(page);
        // If same page, increment fetchVersion to force refetch
        setFetchVersion(v => v + 1);
    }, []);

    const setError = useCallback((err: string | null) => {
        setManualError(err);
    }, []);

    return {
        data: rows,
        listResponse,
        loading,
        error,
        currentPage,
        totalPages,
        totalResults,
        hasNextPage,
        hasPreviousPage,
        setCurrentPage,
        setError,
        reload,
        loadPage
    };
}
